import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './inboxIcons';
import { PRESETS, DEFAULT_ACCENT, buildTheme, contrast, isHex } from './inboxTheme';
import './inbox.css';

// Admin setting: one main colour for the whole team's support workspace
function InboxAppearance({ apiBase, getToken }) {
  const [saved, setSaved] = useState(DEFAULT_ACCENT);
  const [draft, setDraft] = useState(DEFAULT_ACCENT);
  const [hexText, setHexText] = useState(DEFAULT_ACCENT.toUpperCase());
  const [hexErr, setHexErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const call = useCallback(async (method, body) => {
    const token = await getToken();
    const res = await fetch(`${apiBase}/api/v1/support/appearance`, {
      method,
      headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.message || 'Request failed');
    return data;
  }, [apiBase, getToken]);

  useEffect(() => {
    call('GET').then((d) => { setSaved(d.accent); setDraft(d.accent); setHexText(d.accent.toUpperCase()); }).catch(() => {});
  }, [call]);

  const pick = (hex) => { setDraft(hex); setHexText(hex.toUpperCase()); setHexErr(null); setMessage(null); };
  const theme = buildTheme(draft);
  const changed = theme.base.toLowerCase() !== saved.toLowerCase();

  const apply = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const d = await call('PUT', { accent: theme.base });
      setSaved(d.accent);
      setDraft(d.accent);
      setHexText(d.accent.toUpperCase());
      window.dispatchEvent(new CustomEvent('cx-accent', { detail: d.accent }));
      setMessage('Applied for the whole team.');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const ramp = [['50', '--acc-50'], ['100', '--acc-100'], ['200', '--acc-200'], ['Main', '--acc'], ['Hover', '--acc-hover'], ['Text', '--acc-700'], ['Ink', '--ink']];

  return (
    <section className="cx cx-appearance" style={{ ...theme.vars, display: 'block', minHeight: 0 }} aria-labelledby="cx-ap-h">
      <h2 id="cx-ap-h">Main colour</h2>
      <p>Pick one colour for the support workspace. Buttons, selection, links and tints are generated from it, and the greys shift warm or cool to match. Status and channel colours never change.</p>
      <div className="cx-swatches">
        {PRESETS.map(([name, hex]) => (
          <button key={hex} type="button" className="cx-sw" aria-pressed={draft.toLowerCase() === hex} onClick={() => pick(hex)}>
            <i style={{ background: hex }} />{name}
          </button>
        ))}
      </div>
      <div className="cx-custom">
        <input type="color" value={isHex(draft) ? draft : DEFAULT_ACCENT} onChange={(e) => pick(e.target.value)} aria-label="Pick a custom colour" />
        <input
          type="text"
          value={hexText}
          maxLength={7}
          spellCheck={false}
          aria-label="Hex colour"
          onChange={(e) => {
            const v = e.target.value.trim();
            setHexText(v);
            const hex = v.startsWith('#') ? v : `#${v}`;
            if (isHex(hex)) { setDraft(hex.toLowerCase()); setHexErr(null); } else setHexErr('Enter a 6-digit hex colour, like #2457C5');
          }}
        />
        {hexErr && <span className="cx-err">{hexErr}</span>}
      </div>
      <div className="cx-ramp" aria-label="Generated palette">
        {ramp.map(([label, key]) => {
          const hex = theme.vars[key];
          return <div key={key} style={{ background: hex, color: contrast(hex, '#ffffff') >= 4.5 ? '#fff' : theme.vars['--ink'] }}>{label}<br />{hex.toUpperCase()}</div>;
        })}
      </div>
      {theme.notes.map(([kind, text]) => (
        <div key={text} className={`cx-notice ${kind}`}><Icon name={kind === 'ok' ? 'check' : 'alert'} size={16} /><span>{text}</span></div>
      ))}
      <div className="cx-preview">
        <button type="button" className="cx-btn cx-btn-primary"><Icon name="send" />Approve and send</button>
        <button type="button" className="cx-btn">Edit</button>
        <span className="cx-chip cx-c-ai"><Icon name="spark" />AI draft</span>
        <span className="cx-chip cx-c-new">New</span>
        <span className="cx-chip cx-c-waiting">Waiting on customer</span>
        <span className="cx-chip cx-c-resolved">Resolved</span>
        <span className="cx-vitem" aria-current="true" style={{ width: 'auto' }}><Icon name="inbox" size={16} />All open</span>
      </div>
      <div className="cx-actions">
        {message && <span className={message.startsWith('Applied') ? 'cx-hint' : 'cx-err'} style={{ alignSelf: 'center' }}>{message}</span>}
        <button type="button" className="cx-btn cx-btn-ghost" onClick={() => pick(DEFAULT_ACCENT)}>Reset to ink blue</button>
        <button type="button" className="cx-btn cx-btn-primary" onClick={apply} disabled={saving || !changed}>
          <Icon name="users" />{saving ? 'Applying…' : changed ? 'Apply for whole team' : 'Applied'}
        </button>
      </div>
    </section>
  );
}

export default InboxAppearance;
